/**
 * Toolbar attribution for the workspace palettes (RFC-1577).
 *
 * Every click on a workspace toolbar — the canvas bottom palette, the same
 * palette in list view, and list view's left rail — emits `canvas_palette_action`.
 * A top-level click that opens a surface (Add node picker, asset library,
 * download modal, shortcuts, tool menu) mints an `interaction_id`; every
 * follow-up action inside that surface carries the same id with
 * `is_entry: false`, so "Add nodes → Video Generator" reads as two rows joined
 * by one id, and the Video Generator pick is attributable to the palette click
 * (and to which toolbar it came from).
 *
 * The provider sits in WorkspacePageV2 and also watches each surface's open
 * flag, so a `close` is recorded however the surface was dismissed (toggle,
 * X button, backdrop, Escape). Surfaces opened from anywhere else have no
 * active interaction, and their follow-ups are not attributed to a toolbar.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react'
import { createNadDebug } from '../utils/debugLog'
import { track } from './events'
import type {
  CanvasPaletteActionProps,
  PaletteAction,
  PaletteControl,
  PaletteSurface,
  WorkspaceViewMode,
} from './generated/analyticsEvents'

const log = createNadDebug('dw:analytics-events:palette')

/** Controls that open a surface and so own follow-up actions until it closes. */
export type PaletteSurfaceControl = 'add_node' | 'tool' | 'asset_library' | 'download' | 'shortcuts'

export interface PaletteInteraction {
  readonly id: string
  readonly surface: PaletteSurface
  readonly control: PaletteControl
}

type Extra = Pick<CanvasPaletteActionProps, 'option' | 'item_count'>

export interface PaletteAnalytics {
  /**
   * A top-level click that opens `control`'s surface. Starts (and returns) a
   * new interaction. Call only when the surface is about to OPEN — a click
   * that toggles it closed is recorded by the open-flag watcher instead.
   */
  open(surface: PaletteSurface, control: PaletteSurfaceControl, extra?: Extra): PaletteInteraction
  /** A one-shot top-level click that opens nothing (view switch, rail category). */
  instant(surface: PaletteSurface, control: PaletteControl, action: PaletteAction, extra?: Extra): void
  /** The interaction currently owning `control`'s surface, or null. */
  active(control: PaletteSurfaceControl): PaletteInteraction | null
  /**
   * A follow-up action inside a surface. Pass a captured interaction when the
   * action can land after the surface closed (e.g. the OS file picker that
   * Add node opens after closing itself); otherwise the active one is used.
   * No-op when there is no interaction to attribute to.
   */
  step(
    control: PaletteSurfaceControl,
    action: PaletteAction,
    extra?: Extra,
    interaction?: PaletteInteraction | null,
  ): void
  /** Record a close for `control` if it has an active interaction, and clear it. */
  closed(control: PaletteSurfaceControl): void
}

function newInteractionId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    /* fall through */
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`
}

/**
 * Build the tracker. Exported for tests; components use the provider + hook.
 * `getContext` is read at emit time so a long-lived tracker always reports the
 * current project and view.
 */
export function createPaletteAnalytics(
  getContext: () => { projectId: string | null; viewMode: WorkspaceViewMode },
): PaletteAnalytics {
  const activeByControl = new Map<PaletteSurfaceControl, PaletteInteraction>()

  const emit = (
    interaction: PaletteInteraction,
    action: PaletteAction,
    isEntry: boolean,
    extra?: Extra,
  ) => {
    const { projectId, viewMode } = getContext()
    if (!projectId) return
    const props: CanvasPaletteActionProps = {
      project_id: projectId,
      surface: interaction.surface,
      control: interaction.control,
      action,
      interaction_id: interaction.id,
      is_entry: isEntry,
      view_mode: viewMode,
      option: extra?.option,
      item_count: extra?.item_count,
    }
    log({
      event: 'palette-action',
      surface: props.surface,
      control: props.control,
      action,
      interaction_id: props.interaction_id,
      is_entry: isEntry,
      view_mode: viewMode,
      option: props.option,
      item_count: props.item_count,
    })
    track('canvas_palette_action', props)
  }

  return {
    open(surface, control, extra) {
      const interaction: PaletteInteraction = { id: newInteractionId(), surface, control }
      activeByControl.set(control, interaction)
      emit(interaction, 'open', true, extra)
      return interaction
    },
    instant(surface, control, action, extra) {
      emit({ id: newInteractionId(), surface, control }, action, true, extra)
    },
    active(control) {
      return activeByControl.get(control) ?? null
    },
    step(control, action, extra, interaction) {
      const target = interaction ?? activeByControl.get(control)
      if (!target) return
      emit(target, action, false, extra)
    },
    closed(control) {
      const interaction = activeByControl.get(control)
      if (!interaction) return
      activeByControl.delete(control)
      emit(interaction, 'close', false)
    },
  }
}

const PaletteAnalyticsContext = createContext<PaletteAnalytics | null>(null)

export interface PaletteAnalyticsProviderProps {
  projectId: string | null
  viewMode: WorkspaceViewMode
  /** Open flags of the surfaces the palettes own, so every close is recorded. */
  openSurfaces: Partial<Record<PaletteSurfaceControl, boolean>>
  children: ReactNode
}

export function PaletteAnalyticsProvider({
  projectId,
  viewMode,
  openSurfaces,
  children,
}: PaletteAnalyticsProviderProps) {
  const contextRef = useRef({ projectId, viewMode })
  contextRef.current = { projectId, viewMode }
  // One tracker for the provider's lifetime: a stable context value keeps the
  // memoized palette and drawers from re-rendering on every workspace render.
  const tracker = useMemo(() => createPaletteAnalytics(() => contextRef.current), [])

  const { add_node, asset_library, download, shortcuts } = openSurfaces
  useEffect(() => { if (!add_node) tracker.closed('add_node') }, [add_node, tracker])
  useEffect(() => { if (!asset_library) tracker.closed('asset_library') }, [asset_library, tracker])
  useEffect(() => { if (!download) tracker.closed('download') }, [download, tracker])
  useEffect(() => { if (!shortcuts) tracker.closed('shortcuts') }, [shortcuts, tracker])

  return (
    <PaletteAnalyticsContext.Provider value={tracker}>{children}</PaletteAnalyticsContext.Provider>
  )
}

/** The workspace toolbar tracker, or null outside the workspace (no-op there). */
export function usePaletteAnalytics(): PaletteAnalytics | null {
  return useContext(PaletteAnalyticsContext)
}
